import * as z from "zod";
import { defineMutators } from "@rocicorp/zero";
import { userFacingError } from "../authorization";
import { syncAllFacets, writeCommitFile } from "../commitFiles";
import { ensureWritable } from "../copyOnWrite";
import { zql } from "../generatedDrizzleSchema";
import { isWebpageRoute } from "../stepConfig";
import {
  isValidStepTestName,
  MAX_STEP_TEST_BYTES,
  STEP_TEST_CASES_DIR,
  STEP_TEST_CLOCK_SUFFIX,
} from "../stepTests/constants";
import {
  parseRequestFixtures,
  STEP_TEST_REQUESTS_SUFFIX,
  validateRequestFixtures,
} from "../stepTests/requests";
import {
  parseStepTestCapture,
  STEP_TEST_CAPTURE_SENTINEL,
} from "../stepTests/runner";
import {
  seededSerializedError,
  serializeStepTestVolumeFixtureContent,
  toStepTestVolumeFixture,
  validateStepTestVolumeFixtureFiles,
  volumeFixtureRelPath,
  type StepTestVolumeFixtureFile,
} from "../stepTests/volumes";
import { mutator } from "./mutator";
import {
  deleteStepTestClockFile,
  deleteStepTestRequestsFile,
  deleteStepTestVolumeFixtureFiles,
} from "./stepTestCommitFiles";
import { branchSchema, insertUserAction } from "./utils";

const fixtureShape = z.object({
  volume: z.string(),
  path: z.string(),
  content: z.string(),
  encoding: z.enum(["utf8", "base64", "sqlite-dump", "serialized"]).optional(),
});

export const pinStepTestSchema = z
  .object({
    workflowId: z.string(),
    stepDir: z.string(),
    executionId: z.string(),
    name: z.string(),
    input: z.string(),
    expectedOutput: z.string(),
    initialVolumeFiles: z.array(fixtureShape).optional().default([]),
    expectedVolumeFiles: z.array(fixtureShape).optional().default([]),
    frozenTime: z.string().optional(),
    requestFixtures: z.string().optional(),
    // Set when the name is a placeholder the user didn't choose, so the
    // server mutator asks the AI to rename it descriptively after pinning.
    autoName: z.boolean().optional(),
  })
  .merge(branchSchema);

function createPinStepTest(recordUserAction: boolean) {
  return mutator({
    schema: pinStepTestSchema,
    resolve: {
      branch: { kind: "branch", from: "branchId" },
    },
    permissions: [
      {
        permission: "space:write",
        scopeId: ({ args }) => args.branch.workflow.spaceId,
      },
    ],
    fn: async ({ tx, ctx, args }) => {
      const {
        branch,
        stepDir,
        name,
        input,
        initialVolumeFiles,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      } = args;
      const normalized = normalizePinnedStepTestOutput(
        args.expectedOutput,
        args.expectedVolumeFiles
      );
      const expectedOutput = normalized.expectedOutput;
      const expectedVolumeFiles = normalized.expectedVolumeFiles;
      if (!isValidStepTestName(name)) {
        throw userFacingError(
          "Step test names may only contain letters, numbers, dashes, and underscores"
        );
      }
      if (
        input.length > MAX_STEP_TEST_BYTES ||
        expectedOutput.length > MAX_STEP_TEST_BYTES
      ) {
        throw userFacingError(
          "Step test input and output must each be under 256 KB"
        );
      }
      const initialVolumeFilesError =
        validateStepTestVolumeFixtureFiles(initialVolumeFiles);
      if (initialVolumeFilesError)
        throw userFacingError(initialVolumeFilesError);
      const expectedVolumeFilesError =
        validateStepTestVolumeFixtureFiles(expectedVolumeFiles);
      if (expectedVolumeFilesError)
        throw userFacingError(expectedVolumeFilesError);
      const seededError = seededSerializedError(initialVolumeFiles);
      if (seededError) throw userFacingError(seededError);
      if (args.requestFixtures !== undefined) {
        let requestFixtures;
        try {
          requestFixtures = parseRequestFixtures(args.requestFixtures);
        } catch {
          throw userFacingError(
            "The recorded request fixtures couldn’t be parsed"
          );
        }
        const requestFixturesError = validateRequestFixtures(requestFixtures);
        if (requestFixturesError) throw userFacingError(requestFixturesError);
      }
      if (branch.headCommitId) {
        const routes = await tx.run(
          zql.commitRoutes
            .where("commitId", branch.headCommitId)
            .where("stepDir", stepDir)
        );
        if (routes.some((route) => isWebpageRoute(route.routeType))) {
          throw userFacingError("Webpages can’t be pinned as step tests.");
        }
      }
      const { commitId } = await ensureWritable(
        tx,
        ctx,
        ctx.tenantId,
        {
          branchId: branch.id,
          forkBranchId,
          forkCommitId,
          forkChatId,
          workingCommitId,
          workingChatId,
        },
        branch
      );
      const base = `${stepDir}/${STEP_TEST_CASES_DIR}/${name}`;
      const writeCtx = { tx, commitId, tenantId: ctx.tenantId };
      await deleteStepTestVolumeFixtureFiles({ tx, commitId, stepDir, name });
      await writeCommitFile(writeCtx, `${base}.input`, input);
      await writeCommitFile(writeCtx, `${base}.output`, expectedOutput);
      await writeCommitFile(writeCtx, `${base}.meta`, args.executionId);
      await deleteStepTestClockFile({ tx, commitId, stepDir, name });
      await deleteStepTestRequestsFile({ tx, commitId, stepDir, name });
      if (args.frozenTime !== undefined) {
        await writeCommitFile(
          writeCtx,
          `${base}${STEP_TEST_CLOCK_SUFFIX}`,
          args.frozenTime
        );
      }
      if (args.requestFixtures !== undefined) {
        await writeCommitFile(
          writeCtx,
          `${base}${STEP_TEST_REQUESTS_SUFFIX}`,
          args.requestFixtures
        );
      }
      await writeVolumeFixtureFiles({
        writeCtx,
        stepDir,
        name,
        phase: "initial",
        files: initialVolumeFiles,
      });
      await writeVolumeFixtureFiles({
        writeCtx,
        stepDir,
        name,
        phase: "expected",
        files: expectedVolumeFiles,
      });
      await syncAllFacets(writeCtx);
      if (recordUserAction) {
        await insertUserAction(tx, commitId, ctx, ctx.tenantId, "pinStepTest", {
          stepDir,
          name,
        });
      }
    },
  });
}

export const pinStepTest = createPinStepTest(true);
export const { pinStepTestWithoutUserAction } = defineMutators({
  pinStepTestWithoutUserAction: createPinStepTest(false),
});

export function normalizePinnedStepTestOutput(
  expectedOutput: string,
  expectedVolumeFiles: StepTestVolumeFixtureFile[] | undefined
) {
  // Server mutators receive args without the schema's Zod defaults applied, so
  // this field can be absent even though its type says otherwise.
  const files = expectedVolumeFiles ?? [];
  if (!expectedOutput.includes(`${STEP_TEST_CAPTURE_SENTINEL}\t`)) {
    return { expectedOutput, expectedVolumeFiles: files };
  }
  try {
    const capture = parseStepTestCapture(expectedOutput);
    if (capture.status !== 0) {
      return { expectedOutput, expectedVolumeFiles: files };
    }
    return {
      expectedOutput: capture.stdout,
      expectedVolumeFiles: files.length > 0 ? files : capture.volumeFiles,
    };
  } catch {
    return { expectedOutput, expectedVolumeFiles: files };
  }
}

async function writeVolumeFixtureFiles({
  writeCtx,
  stepDir,
  name,
  phase,
  files,
}: {
  writeCtx: Parameters<typeof writeCommitFile>[0];
  stepDir: string;
  name: string;
  phase: "initial" | "expected";
  files: StepTestVolumeFixtureFile[];
}) {
  for (const file of files) {
    const fixture = toStepTestVolumeFixture(file);
    await writeCommitFile(
      writeCtx,
      `${stepDir}/${volumeFixtureRelPath(name, phase, fixture)}`,
      serializeStepTestVolumeFixtureContent(fixture)
    );
  }
}
